import React from 'react';

import {
  ArrowLeftOutlined,
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ThunderboltOutlined,
  SendOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Space,
  Tag,
  Typography,
  Descriptions,
  List,
  Badge,
  Tooltip,
  Empty,
  Spin,
  Tabs,
} from 'antd';

import { AdProject, GeneratedAd, PAGE_GOALS } from '../types';

const { Title, Text, Paragraph } = Typography;

interface ProjectDetailsProps {
  project: AdProject;
  onBack: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onStartAnalysis: () => void;
  onGenerateAds: () => void;
  onEditAd: (ad: GeneratedAd) => void;
  onSendAd: (ad: GeneratedAd) => void;
}

const getStatusTag = (status: AdProject['status']) => {
  const statusConfig = {
    draft: { color: 'default', text: 'Черновик' },
    analyzing: { color: 'processing', text: 'Анализ...' },
    ready: { color: 'success', text: 'Готов' },
    active: { color: 'green', text: 'Активен' },
    paused: { color: 'warning', text: 'Приостановлен' },
  };
  const config = statusConfig[status];
  return <Tag color={config.color}>{config.text}</Tag>;
};

const getAdStatusTag = (status: GeneratedAd['status']) => {
  const statusConfig = {
    draft: { color: 'default', text: 'Черновик' },
    pending: { color: 'processing', text: 'Ожидает' },
    sent: { color: 'blue', text: 'Отправлено' },
    active: { color: 'green', text: 'Активно' },
    rejected: { color: 'red', text: 'Отклонено' },
    error: { color: 'error', text: 'Ошибка' },
  };
  const config = statusConfig[status];
  return <Tag color={config.color}>{config.text}</Tag>;
};

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  onBack,
  onEdit,
  onToggleStatus,
  onStartAnalysis,
  onGenerateAds,
  onEditAd,
  onSendAd,
}) => {
  const goalLabel = PAGE_GOALS.find(g => g.value === project.pageGoal)?.label || project.pageGoal;

  // Получаем смысловую единицу по ID
  const getSemanticUnitById = (id: string) => {
    for (const sphere of project.lifeSpheres) {
      for (const theme of sphere.themes) {
        const unit = theme.semanticUnits.find(su => su.id === id);
        if (unit) return { unit, theme, sphere };
      }
    }
    return null;
  };

  // Вкладка: Информация о продукте
  const infoTab = (
    <div style={{ padding: '16px 0' }}>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="Название проекта" span={2}>
          <strong>{project.name}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="URL страницы" span={2}>
          <a href={project.landingUrl} target="_blank" rel="noopener noreferrer">
            {project.landingUrl}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Задача страницы">{goalLabel}</Descriptions.Item>
        <Descriptions.Item label="Режим">
          <Tag color={project.mode === 'auto' ? 'blue' : 'purple'}>
            {project.mode === 'auto' ? 'Автоматический' : 'Ручной'}
          </Tag>
        </Descriptions.Item>
      </Descriptions>

      <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>Платный продукт</Title>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="Название продукта" span={2}>
          <strong>{project.paidProduct.name}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Ключевая мысль" span={2}>
          {project.paidProduct.keyIdea}
        </Descriptions.Item>
        <Descriptions.Item label="Средний чек">
          {project.paidProduct.averageCheck 
            ? `${project.paidProduct.averageCheck.toLocaleString()} ₽` 
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="URL продающего сайта">
          {project.paidProduct.salesPageUrl ? (
            <a href={project.paidProduct.salesPageUrl} target="_blank" rel="noopener noreferrer">
              {project.paidProduct.salesPageUrl}
            </a>
          ) : '—'}
        </Descriptions.Item>
      </Descriptions>

      {(project.companyInfo.companyName || project.companyInfo.authorName) && (
        <>
          <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>О компании и авторе</Title>
          <Descriptions column={2} bordered size="small">
            {project.companyInfo.companyName && (
              <Descriptions.Item label="Компания">
                {project.companyInfo.companyName}
              </Descriptions.Item>
            )}
            {project.companyInfo.companyActivity && (
              <Descriptions.Item label="Деятельность">
                {project.companyInfo.companyActivity}
              </Descriptions.Item>
            )}
            {project.companyInfo.authorName && (
              <Descriptions.Item label="Автор">
                {project.companyInfo.authorName}
              </Descriptions.Item>
            )}
            {project.companyInfo.authorDescription && (
              <Descriptions.Item label="Описание автора" span={2}>
                {project.companyInfo.authorDescription}
              </Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}

      <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>Настройки</Title>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Ключевые слова">
          {project.settings.keywords.length > 0 ? (
            <Space wrap>
              {project.settings.keywords.map((word, i) => (
                <Tag key={i} color="blue">{word}</Tag>
              ))}
            </Space>
          ) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Слова-исключения">
          {project.settings.excludeWords.length > 0 ? (
            <Space wrap>
              {project.settings.excludeWords.map((word, i) => (
                <Tag key={i} color="red">{word}</Tag>
              ))}
            </Space>
          ) : '—'}
        </Descriptions.Item>
        {project.notes && (
          <Descriptions.Item label="Заметки">
            {project.notes}
          </Descriptions.Item>
        )}
      </Descriptions>
    </div>
  );

  // Вкладка: Карта смыслов
  const semanticsTab = (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={project.status === 'analyzing' ? <Spin size="small" /> : <ExperimentOutlined />}
          onClick={onStartAnalysis}
          loading={project.status === 'analyzing'}
          disabled={project.status === 'analyzing'}
        >
          {project.status === 'analyzing' ? 'Анализ...' : 'Запустить анализ'}
        </Button>
      </div>

      {project.lifeSpheres.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Карта смыслов пока не создана. Запустите анализ проекта."
        >
          <Button type="primary" icon={<ExperimentOutlined />} onClick={onStartAnalysis}>
            Запустить анализ
          </Button>
        </Empty>
      ) : (
        <div>
          {project.lifeSpheres.map((sphere) => (
            <Card
              key={sphere.id}
              title={
                <Space>
                  <Tag color="purple" style={{ fontWeight: 'bold' }}>{sphere.code}</Tag>
                  <span style={{ fontSize: 16 }}>{sphere.name}</span>
                </Space>
              }
              size="small"
              style={{ marginBottom: 16 }}
            >
              {sphere.themes.map((theme) => (
                <div key={theme.id} style={{ marginBottom: 20 }}>
                  <div style={{ 
                    marginBottom: 12, 
                    padding: '8px 12px', 
                    background: '#f5f5f5', 
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Text strong style={{ fontSize: 14 }}>{theme.name}</Text>
                    <Tag color="cyan">Релевантность: {theme.relevance}</Tag>
                  </div>
                  <List
                    size="small"
                    dataSource={theme.semanticUnits}
                    renderItem={(unit) => (
                      <List.Item style={{ padding: '12px 16px' }}>
                        <List.Item.Meta
                          title={
                            <Space>
                              <Text strong>{unit.title}</Text>
                              <Tag color="green">{unit.weight}</Tag>
                            </Space>
                          }
                          description={
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {unit.description}
                            </Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Вкладка: Генерация рекламы
  const adsTab = (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={onGenerateAds}
          disabled={project.lifeSpheres.length === 0}
        >
          Сгенерировать объявления
        </Button>
      </div>

      {project.generatedAds.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            project.lifeSpheres.length === 0
              ? 'Сначала запустите анализ для создания карты смыслов'
              : 'Объявления пока не сгенерированы'
          }
        >
          {project.lifeSpheres.length > 0 && (
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={onGenerateAds}>
              Сгенерировать объявления
            </Button>
          )}
        </Empty>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          dataSource={project.generatedAds}
          renderItem={(ad) => {
            const semanticInfo = getSemanticUnitById(ad.semanticUnitId);
            return (
              <List.Item>
                <Card
                  size="small"
                  title={
                    <Space>
                      <Tag color={ad.platform === 'yandex_direct' ? 'orange' : 'blue'}>
                        {ad.platform === 'yandex_direct' ? 'Яндекс' : 'ВК'}
                      </Tag>
                      {getAdStatusTag(ad.status)}
                    </Space>
                  }
                  extra={
                    <Space>
                      <Tooltip title="Редактировать">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => onEditAd(ad)}
                        />
                      </Tooltip>
                      {ad.status === 'draft' && (
                        <Tooltip title="Отправить в рекламную систему">
                          <Button
                            type="text"
                            size="small"
                            icon={<SendOutlined />}
                            onClick={() => onSendAd(ad)}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  }
                >
                  {semanticInfo && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {semanticInfo.sphere.name} → {semanticInfo.theme.name} → {semanticInfo.unit.title}
                      </Text>
                    </div>
                  )}
                  <Title level={5} style={{ marginBottom: 4, fontSize: 14 }}>{ad.headline}</Title>
                  {ad.subheadline && (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                      {ad.subheadline}
                    </Text>
                  )}
                  <Paragraph
                    ellipsis={{ rows: 3, expandable: true, symbol: 'ещё' }}
                    style={{ marginBottom: 8, fontSize: 13 }}
                  >
                    {ad.text}
                  </Paragraph>
                  {ad.clarifications.length > 0 && (
                    <div>
                      <Space wrap size={4}>
                        {ad.clarifications.map((c, i) => (
                          <Tag key={i} color="green" style={{ fontSize: 11 }}>{c}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  // Конфигурация вкладок
  const tabItems = [
    {
      key: 'info',
      label: 'Информация о продукте',
      children: infoTab,
    },
    {
      key: 'semantics',
      label: (
        <Space>
          Карта смыслов
          {project.lifeSpheres.length > 0 && (
            <Badge
              count={project.lifeSpheres.reduce(
                (acc, sf) => acc + sf.themes.reduce((a, t) => a + t.semanticUnits.length, 0),
                0
              )}
              style={{ backgroundColor: '#52c41a' }}
            />
          )}
        </Space>
      ),
      children: semanticsTab,
    },
    {
      key: 'ads',
      label: (
        <Space>
          Генерация рекламы
          {project.generatedAds.length > 0 && (
            <Badge count={project.generatedAds.length} style={{ backgroundColor: '#1890ff' }} />
          )}
        </Space>
      ),
      children: adsTab,
    },
  ];

  return (
    <div>
      {/* Заголовок и действия */}
      <div style={{ marginBottom: 24 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Назад к списку
          </Button>
        </Space>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={3} style={{ marginBottom: 8 }}>
              {project.name} {getStatusTag(project.status)}
            </Title>
            <Text type="secondary">
              {goalLabel} • {project.mode === 'auto' ? 'Автоматический режим' : 'Ручной режим'}
            </Text>
          </div>
          <Space>
            <Button icon={<EditOutlined />} onClick={onEdit}>
              Редактировать
            </Button>
            <Button
              icon={project.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={onToggleStatus}
            >
              {project.status === 'active' ? 'Приостановить' : 'Запустить'}
            </Button>
          </Space>
        </div>
      </div>

      {/* Вкладки */}
      <Tabs
        items={tabItems}
        type="card"
        size="large"
      />
    </div>
  );
};

export default ProjectDetails;
